import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { virtualClassroomsApi, teachersApi, trainingSlidesApi, uploadFile } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Video, Plus, Pencil, Trash2, ExternalLink, Calendar, Clock, Users, Upload, FileText } from 'lucide-react';

export default function VirtualClassroomsPage() {
  const [classrooms, setClassrooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [slides, setSlides] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [slidesDialogOpen, setSlidesDialogOpen] = useState(false);
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [editingClassroom, setEditingClassroom] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    teacher_id: '',
    zoom_link: '',
    zoom_meeting_id: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    max_participants: 50
  });

  const [slideForm, setSlideForm] = useState({
    title: '',
    description: '',
    file: null
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [classroomsRes, teachersRes] = await Promise.all([
        virtualClassroomsApi.getAll(),
        teachersApi.getAll()
      ]);
      setClassrooms(classroomsRes.data);
      setTeachers(teachersRes.data);
      
      // Load slides for each classroom
      const slidesMap = {};
      for (const classroom of classroomsRes.data) {
        const slidesRes = await trainingSlidesApi.getAll({ classroom_id: classroom.id });
        slidesMap[classroom.id] = slidesRes.data;
      }
      setSlides(slidesMap);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      teacher_id: '',
      zoom_link: '',
      zoom_meeting_id: '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 60,
      max_participants: 50
    });
    setEditingClassroom(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClassroom) {
        await virtualClassroomsApi.update(editingClassroom.id, formData);
        toast.success('Aula virtuale aggiornata');
      } else {
        await virtualClassroomsApi.create(formData);
        toast.success('Aula virtuale creata');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEdit = (classroom) => {
    setEditingClassroom(classroom);
    setFormData({
      name: classroom.name || '',
      description: classroom.description || '',
      teacher_id: classroom.teacher_id || '',
      zoom_link: classroom.zoom_link || '',
      zoom_meeting_id: classroom.zoom_meeting_id || '',
      scheduled_date: classroom.scheduled_date || '',
      scheduled_time: classroom.scheduled_time || '',
      duration_minutes: classroom.duration_minutes || 60,
      max_participants: classroom.max_participants || 50
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Sei sicuro di voler eliminare questa aula virtuale?')) return;
    try {
      await virtualClassroomsApi.delete(id);
      toast.success('Aula virtuale eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleUploadSlide = async (e) => {
    e.preventDefault();
    if (!slideForm.file || !selectedClassroom) return;
    
    setUploading(true);
    try {
      const uploadRes = await uploadFile('slides', slideForm.file);
      
      const teacher = teachers.find(t => t.id === selectedClassroom.teacher_id);
      
      await trainingSlidesApi.create({
        teacher_id: selectedClassroom.teacher_id || teachers[0]?.id || '',
        classroom_id: selectedClassroom.id,
        title: slideForm.title,
        description: slideForm.description,
        file_url: uploadRes.data.url,
        file_type: slideForm.file.name.split('.').pop()
      });
      
      toast.success('Slide caricata');
      setSlidesDialogOpen(false);
      setSlideForm({ title: '', description: '', file: null });
      loadData();
    } catch (error) {
      toast.error('Errore nel caricamento della slide');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteSlide = async (slideId) => {
    if (!confirm('Eliminare questa slide?')) return;
    try {
      await trainingSlidesApi.delete(slideId);
      toast.success('Slide eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher?.name || 'Non assegnato';
  };

  const statusColors = {
    scheduled: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-green-100 text-green-800',
    completed: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  const statusLabels = {
    scheduled: 'Programmata',
    in_progress: 'In corso',
    completed: 'Completata',
    cancelled: 'Annullata'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl">Aule Virtuali</h1>
          <p className="text-muted-foreground mt-1">Gestisci le sessioni di formazione online e le slide</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-classroom-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Aula
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingClassroom ? 'Modifica Aula' : 'Nuova Aula Virtuale'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome Aula *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="es. Corso Sicurezza Base"
                />
              </div>
              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Docente</Label>
                <Select value={formData.teacher_id} onValueChange={(v) => setFormData({...formData, teacher_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona docente" />
                  </SelectTrigger>
                  <SelectContent>
                    {teachers.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Link Zoom</Label>
                <Input
                  value={formData.zoom_link}
                  onChange={(e) => setFormData({...formData, zoom_link: e.target.value})}
                  placeholder="https://zoom.us/j/..."
                />
                <p className="text-xs text-muted-foreground">Inserisci manualmente il link della riunione Zoom</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data</Label>
                  <Input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ora</Label>
                  <Input
                    type="time"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Durata (minuti)</Label>
                  <Input
                    type="number"
                    min="15"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Partecipanti</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({...formData, max_participants: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              <Button type="submit" className="w-full">
                {editingClassroom ? 'Salva Modifiche' : 'Crea Aula'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {classrooms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Video className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">Nessuna aula virtuale</h3>
            <p className="text-muted-foreground">Crea la prima aula virtuale per iniziare</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {classrooms.map((classroom, index) => (
            <motion.div
              key={classroom.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Video className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{classroom.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[classroom.status]}`}>
                          {statusLabels[classroom.status]}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(classroom)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(classroom.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  
                  {classroom.description && (
                    <p className="text-sm text-muted-foreground mb-4">{classroom.description}</p>
                  )}
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="w-4 h-4" />
                      Docente: {getTeacherName(classroom.teacher_id)}
                    </div>
                    {classroom.scheduled_date && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {new Date(classroom.scheduled_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        {classroom.scheduled_time && ` alle ${classroom.scheduled_time}`}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      Durata: {classroom.duration_minutes} minuti
                    </div>
                  </div>
                  
                  {classroom.zoom_link && (
                    <a
                      href={classroom.zoom_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 mt-4 text-sm text-primary hover:underline"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Apri Link Zoom
                    </a>
                  )}
                  
                  {/* Slides section */}
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Slide ({slides[classroom.id]?.length || 0})</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedClassroom(classroom);
                          setSlidesDialogOpen(true);
                        }}
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Carica
                      </Button>
                    </div>
                    {slides[classroom.id]?.length > 0 && (
                      <div className="space-y-1">
                        {slides[classroom.id].map(slide => (
                          <div key={slide.id} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              <span className="truncate max-w-[150px]">{slide.title}</span>
                            </div>
                            <div className="flex gap-1">
                              <a href={`${slide.file_url}`} target="_blank" rel="noopener noreferrer">
                                <Button variant="ghost" size="sm">
                                  <ExternalLink className="w-3 h-3" />
                                </Button>
                              </a>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteSlide(slide.id)}>
                                <Trash2 className="w-3 h-3 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Upload Slide Dialog */}
      <Dialog open={slidesDialogOpen} onOpenChange={setSlidesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carica Slide</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadSlide} className="space-y-4">
            <div className="space-y-2">
              <Label>Titolo *</Label>
              <Input
                value={slideForm.title}
                onChange={(e) => setSlideForm({...slideForm, title: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input
                value={slideForm.description}
                onChange={(e) => setSlideForm({...slideForm, description: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>File *</Label>
              <Input
                type="file"
                accept=".pdf,.ppt,.pptx,.doc,.docx"
                onChange={(e) => setSlideForm({...slideForm, file: e.target.files[0]})}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={uploading}>
              {uploading ? 'Caricamento...' : 'Carica Slide'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
